/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the application.
 * Handles login, logout, token refresh, and user state management.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';

// =============================================================================
// Types
// =============================================================================

/**
 * User role enum matching backend Role enum
 */
export type Role = 'GLOBAL_ADMIN' | 'TENANT_ADMIN' | 'TENANT_USER' | 'VIEWER';

/**
 * Authenticated user information
 */
export interface User {
  id: string;
  email: string;
  name: string;
  role: Role;
  tenantId: string | null;
}

/**
 * Authentication state
 */
export interface AuthState {
  /** Current authenticated user */
  user: User | null;
  /** Whether authentication check is in progress */
  isLoading: boolean;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Current access token */
  accessToken: string | null;
  /** Token expiration time */
  expiresAt: Date | null;
}

/**
 * Login credentials
 */
export interface LoginCredentials {
  email: string;
  password: string;
}

/**
 * Authentication error
 */
export interface AuthError {
  code: string;
  message: string;
  details?: Record<string, string[]>;
}

/**
 * Login result
 */
export type LoginResult =
  | { success: true; user: User }
  | { success: false; error: AuthError };

/**
 * Authentication context value
 */
export interface AuthContextValue extends AuthState {
  /** Log in with email and password */
  login: (credentials: LoginCredentials) => Promise<LoginResult>;
  /** Log out the current user */
  logout: () => Promise<void>;
  /** Refresh the access token */
  refreshToken: () => Promise<boolean>;
  /** Clear any auth errors */
  clearError: () => void;
  /** Current auth error, if any */
  error: AuthError | null;
}

// =============================================================================
// API Types
// =============================================================================

interface ApiAuthResponse {
  success: true;
  data: {
    user: User;
    accessToken: string;
    refreshToken: string;
    expiresAt: string;
  };
}

interface ApiVerifyResponse {
  success: true;
  data: {
    valid: true;
    user: User;
    expiresAt: string;
  };
}

interface ApiRefreshResponse {
  success: true;
  data: {
    accessToken: string;
    expiresAt: string;
  };
}

interface ApiErrorResponse {
  success: false;
  error: AuthError;
}

// =============================================================================
// Constants
// =============================================================================

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const TOKEN_STORAGE_KEY = 'auth_token';
const REFRESH_TOKEN_STORAGE_KEY = 'refresh_token';
const TOKEN_EXPIRY_KEY = 'token_expiry';

// Refresh token 5 minutes before expiry
const TOKEN_REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

// =============================================================================
// Module-level deduplication (prevents concurrent API calls)
// =============================================================================

let refreshPromise: Promise<boolean> | null = null;
let verifyPromise: Promise<boolean> | null = null;

// =============================================================================
// Context
// =============================================================================

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// =============================================================================
// Provider
// =============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [error, setError] = useState<AuthError | null>(null);

  // Track if initialization has run
  const isInitializedRef = useRef(false);

  const isAuthenticated = !!user && !!accessToken;

  /**
   * Store authentication data in local storage
   */
  const storeAuthData = useCallback(
    (token: string, refreshToken: string, expiry: Date) => {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiry.toISOString());
    },
    []
  );

  /**
   * Clear authentication data from local storage
   */
  const clearAuthData = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  }, []);

  /**
   * Get stored authentication data
   */
  const getStoredAuthData = useCallback(() => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
    const expiry = expiryStr ? new Date(expiryStr) : null;

    return { token, refreshToken, expiry };
  }, []);

  /**
   * Make authenticated API request
   */
  const apiRequest = useCallback(
    async <T,>(
      endpoint: string,
      options: RequestInit = {}
    ): Promise<T | ApiErrorResponse> => {
      const { token } = getStoredAuthData();

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      };

      if (token) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
      }

      try {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          ...options,
          headers,
          credentials: 'include', // Include cookies for HTTP-only token support
        });

        const data = (await response.json()) as T | ApiErrorResponse;
        return data;
      } catch (err) {
        return {
          success: false,
          error: {
            code: 'NETWORK_ERROR',
            message: 'Unable to connect to the server',
          },
        };
      }
    },
    [getStoredAuthData]
  );

  /**
   * Verify the current token and get user info
   */
  const verifyAuth = useCallback(async (): Promise<boolean> => {
    // Return existing promise if verification is in progress (deduplication)
    if (verifyPromise) {
      return verifyPromise;
    }

    const { token } = getStoredAuthData();

    if (!token) {
      return false;
    }

    const doVerify = async (): Promise<boolean> => {
      try {
        const result = await apiRequest<ApiVerifyResponse>('/auth/verify', {
          method: 'GET',
        });

        if (result.success === true) {
          setUser(result.data.user);
          setAccessToken(token);
          setExpiresAt(new Date(result.data.expiresAt));
          return true;
        }

        // Don't clear auth on rate limit or network errors
        const errorCode = result.error?.code;
        if (errorCode === 'RATE_LIMIT_EXCEEDED' || errorCode === 'NETWORK_ERROR') {
          return false;
        }

        // Token invalid, clear auth data
        clearAuthData();
        return false;
      } finally {
        verifyPromise = null;
      }
    };

    verifyPromise = doVerify();
    return verifyPromise;
  }, [apiRequest, getStoredAuthData, clearAuthData]);

  /**
   * Refresh the access token
   */
  const refreshToken = useCallback(async (): Promise<boolean> => {
    // Return existing promise if refresh is in progress (deduplication)
    if (refreshPromise) {
      return refreshPromise;
    }

    const { refreshToken: storedRefreshToken } = getStoredAuthData();

    if (!storedRefreshToken) {
      return false;
    }

    const doRefresh = async (): Promise<boolean> => {
      try {
        const result = await apiRequest<ApiRefreshResponse>('/auth/refresh', {
          method: 'POST',
          body: JSON.stringify({ refreshToken: storedRefreshToken }),
        });

        if (result.success === true) {
          const newExpiry = new Date(result.data.expiresAt);
          setAccessToken(result.data.accessToken);
          setExpiresAt(newExpiry);

          // Update stored token but keep refresh token
          localStorage.setItem(TOKEN_STORAGE_KEY, result.data.accessToken);
          localStorage.setItem(TOKEN_EXPIRY_KEY, newExpiry.toISOString());

          return true;
        }

        // Don't clear auth on rate limit or network errors - only on actual auth failures
        const errorCode = result.error?.code;
        if (errorCode === 'RATE_LIMIT_EXCEEDED' || errorCode === 'NETWORK_ERROR') {
          // Temporary failure, keep existing auth state
          return false;
        }

        // Actual auth failure (invalid/expired refresh token), clear auth data
        clearAuthData();
        setUser(null);
        setAccessToken(null);
        setExpiresAt(null);

        return false;
      } finally {
        refreshPromise = null;
      }
    };

    refreshPromise = doRefresh();
    return refreshPromise;
  }, [apiRequest, getStoredAuthData, clearAuthData]);

  /**
   * Log in with email and password
   */
  const login = useCallback(
    async (credentials: LoginCredentials): Promise<LoginResult> => {
      setError(null);
      setIsLoading(true);

      try {
        const result = await apiRequest<ApiAuthResponse>('/auth/login', {
          method: 'POST',
          body: JSON.stringify(credentials),
        });

        if (result.success === true) {
          const { user: userData, accessToken: token, refreshToken: refresh, expiresAt: expiry } = result.data;
          const expiryDate = new Date(expiry);

          setUser(userData);
          setAccessToken(token);
          setExpiresAt(expiryDate);
          storeAuthData(token, refresh, expiryDate);

          return { success: true, user: userData };
        }

        // result.success === false
        setError(result.error);
        return { success: false, error: result.error };
      } finally {
        setIsLoading(false);
      }
    },
    [apiRequest, storeAuthData]
  );

  /**
   * Log out the current user
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      // Call logout endpoint to clear server-side session/cookies
      await apiRequest('/auth/logout', { method: 'POST' });
    } catch {
      // Ignore errors, proceed with local cleanup
    }

    clearAuthData();
    setUser(null);
    setAccessToken(null);
    setExpiresAt(null);
    setError(null);
  }, [apiRequest, clearAuthData]);

  /**
   * Clear any auth errors
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Initialize auth state on mount - runs only once
   */
  useEffect(() => {
    // Prevent multiple initializations
    if (isInitializedRef.current) {
      return;
    }
    isInitializedRef.current = true;

    const initAuth = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem(TOKEN_STORAGE_KEY);
        const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
        const expiry = expiryStr ? new Date(expiryStr) : null;

        if (token && expiry) {
          const now = new Date();
          const timeUntilExpiry = expiry.getTime() - now.getTime();

          if (timeUntilExpiry > TOKEN_REFRESH_THRESHOLD_MS) {
            // Token still valid, verify it
            const isValid = await verifyAuth();
            if (!isValid) {
              // Try to refresh
              await refreshToken();
            }
          } else if (timeUntilExpiry > 0) {
            // Token about to expire, refresh it
            const refreshed = await refreshToken();
            if (refreshed) {
              await verifyAuth();
            }
          } else {
            // Token expired, try to refresh
            const refreshed = await refreshToken();
            if (refreshed) {
              await verifyAuth();
            }
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    void initAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Set up token refresh interval
   */
  useEffect(() => {
    if (!expiresAt || !isAuthenticated) {
      return;
    }

    const timeUntilRefresh =
      expiresAt.getTime() - Date.now() - TOKEN_REFRESH_THRESHOLD_MS;

    // Don't set up refresh if time is invalid
    if (timeUntilRefresh <= 0) {
      return;
    }

    const refreshTimeout = setTimeout(() => {
      void refreshToken();
    }, timeUntilRefresh);

    return () => {
      clearTimeout(refreshTimeout);
    };
  // Only re-run when expiresAt actually changes to a new value
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiresAt?.getTime(), isAuthenticated]);

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated,
    accessToken,
    expiresAt,
    error,
    login,
    logout,
    refreshToken,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook to access authentication context
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Check if user has a specific role
 */
export function hasRole(user: User | null, role: Role): boolean {
  if (!user) return false;
  return user.role === role;
}

/**
 * Check if user has minimum role level
 * Role hierarchy: GLOBAL_ADMIN > TENANT_ADMIN > TENANT_USER > VIEWER
 */
export function hasMinimumRole(user: User | null, minimumRole: Role): boolean {
  if (!user) return false;

  const roleHierarchy: Record<Role, number> = {
    GLOBAL_ADMIN: 4,
    TENANT_ADMIN: 3,
    TENANT_USER: 2,
    VIEWER: 1,
  };

  return roleHierarchy[user.role] >= roleHierarchy[minimumRole];
}

/**
 * Check if user is a global admin
 */
export function isGlobalAdmin(user: User | null): boolean {
  return hasRole(user, 'GLOBAL_ADMIN');
}

/**
 * Check if user is at least a tenant admin
 */
export function isTenantAdmin(user: User | null): boolean {
  return hasMinimumRole(user, 'TENANT_ADMIN');
}

export default AuthContext;
